import React, { ReactNode, useEffect } from 'react'
import { Overlay, Container, Header, Content, Footer } from './BottomSheet.styles'
import BottomSheetHeader from '../BottomSheetHeader/BottomSheetHeader'

export interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    height?: string;
    children: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    showCloseButton?: boolean;
    overlayOpacity?: number;
    closeButtonLabel: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    title = '',
    height = '60%',
    children,
    header,
    footer,
    showCloseButton = true,
    overlayOpacity = 0.5,
    closeButtonLabel,
}) => {
    // Handle ESC key and prevent body scrolling
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
            // Prevent body scrolling when bottom sheet is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Default header with title and close button
    const defaultHeader = (
        <Header>
            <span>{title}</span>
            {showCloseButton && (
                <span
                    onClick={onClose}
                    style={{ cursor: 'pointer', fontSize: '14px' }}
                >
                    {closeButtonLabel}
                </span>
            )}
        </Header>
    );

    // We'll use a wrapper function to handle the header rendering
    const renderHeader = () => {
        // If no custom header is provided, use the default one
        if (!header) return defaultHeader;

        // If it's a BottomSheetHeader component, we need to wrap it to pass our onClose function
        if (React.isValidElement(header) && header.type === BottomSheetHeader) {
            const { children: headerChildren, ...headerProps } = header.props as { children?: ReactNode; [key: string]: unknown };
            return (
                <BottomSheetHeader {...headerProps} onSwipeDown={onClose}>
                    {headerChildren}
                </BottomSheetHeader>
            );
        }

        // Otherwise, just return the header as is
        return header;
    };

    return (
        <>
            <Overlay $isOpen={isOpen} onClick={onClose} $overlayOpacity={overlayOpacity} />
            <Container $isOpen={isOpen} $height={height}>
                {renderHeader()}

                <Content>
                    {children}
                </Content>

                {footer && (
                    <Footer>
                        {footer}
                    </Footer>
                )}
            </Container>
        </>
    )
}

export default BottomSheet
